import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Trash2, Edit, Search, RefreshCw, UserPlus,
    ExternalLink, Wallet, CalendarCheck
} from 'lucide-react';
import AddEmployee from './AddEmployee';
import EmployeeDashboard from './EmployeeDashboard';
import AttendanceManager from './AttendanceManager';

// ✅ Custom API instance import kiya
import api from '../api';

const StaffList = () => {
    const [staff, setStaff] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const [isAttendanceView, setIsAttendanceView] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [isSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
    const [payrollDetails, setPayrollDetails] = useState(null);

    // ✅ Images ke liye base path (Backend folder reference)
    const IMAGE_BASE = '/uploads';

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        fetchStaff();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchStaff = async () => {
        setLoading(true);
        try {
            // ✅ api instance use kiya
            const res = await api.get('/employees');
            setStaff(res.data);
        } catch (err) {
            console.error("Error fetching staff:", err);
        } finally {
            setLoading(false);
        }
    };

    // --- Handlers ---

    const handleGiveAdvance = async (empId) => {
        const amount = window.prompt("Enter Advance Amount to Give (₹):");
        if (!amount || isNaN(amount) || Number(amount) <= 0) return;
        try {
            // ✅ api instance use kiya
            await api.put(`/employee/advance/${empId}`, {
                amount: -Math.abs(Number(amount))
            });
            alert(`₹${amount} Advance added successfully!`);
            fetchStaff();
        } catch (err) {
            alert("Error updating advance!");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("⚠️ Are you sure you want to delete this employee?")) {
            try {
                // ✅ FIXED: Yahan API_BASE ki jagah api.delete use kiya
                await api.delete(`/employee/${id}`);
                alert("✅ Employee deleted successfully");
                fetchStaff();
            } catch (err) {
                console.error("Delete error:", err);
                alert("❌ Error deleting employee!");
            }
        }
    };

    const handleEditClick = (employee) => {
        setEditingEmployee(employee);
        setIsEditModalOpen(true);
    };

    const handleViewDashboard = (emp) => {
        setPayrollDetails(emp);
        setIsSalaryModalOpen(true);
    };

    const filteredStaff = staff.filter(s =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.employeeId?.toString().includes(searchTerm) ||
        (s.post && s.post.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // --- Views Logic (Conditional Rendering) ---

    if (isAttendanceView) {
        return (
            <div style={styles.container}>
                <button
                    onClick={() => setIsAttendanceView(false)}
                    style={{ ...styles.btnSync, margin: '20px', marginBottom: '10px', padding: '10px 20px', fontWeight: '700' }}
                >
                    ← Back to Staff List
                </button>
                <AttendanceManager />
            </div>
        );
    }

    if (isEditModalOpen) {
        return (
            <AddEmployee
                initialData={editingEmployee}
                onBack={() => {
                    setIsEditModalOpen(false);
                    setEditingEmployee(null);
                }}
                onEmployeeAdded={() => {
                    setIsEditModalOpen(false);
                    setEditingEmployee(null);
                    fetchStaff();
                }}
            />
        );
    }

    return (
        <div style={{ ...styles.container, padding: isMobile ? '12px' : '24px' }}>
            {/* Header Area */}
            <div style={styles.headerCard}>
                <div style={{ ...styles.headerFlex, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center' }}>
                    <div style={{ marginBottom: isMobile ? '10px' : '0' }}>
                        <h4 style={styles.title}>Staff Management</h4>
                        <p style={styles.subtitle}>{staff.length} Employees Registered</p>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', width: isMobile ? '100%' : 'auto' }}>
                        <button
                            onClick={() => setIsAttendanceView(true)}
                            style={{ ...styles.btnAdd, backgroundColor: '#10b981', flex: 1 }}
                        >
                            <CalendarCheck size={16} /> Attendance
                        </button>

                        <button onClick={fetchStaff} style={styles.btnSync}>
                            <RefreshCw size={16} className={loading ? 'spin' : ''} />
                        </button>

                        <button onClick={() => setIsEditModalOpen(true)} style={{ ...styles.btnAdd, flex: 1 }}>
                            <UserPlus size={16} /> Add Staff
                        </button>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div style={styles.searchBarContainer}>
                <div style={styles.searchWrapper}>
                    <Search size={18} style={styles.searchIcon} />
                    <input
                        type="text"
                        style={styles.searchInput}
                        placeholder="Search name, ID or designation..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Content Table */}
            <div style={isMobile ? styles.mobileContainer : styles.tableCard}>
                {loading ? (
                    <div style={styles.emptyMsg}>⌛ Loading staff data...</div>
                ) : filteredStaff.length > 0 ? (
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <table style={{ ...styles.table, minWidth: isMobile ? '700px' : '100%' }}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Employee</th>
                                    <th style={styles.th}>Designation</th>
                                    <th style={styles.th}>Documents</th>
                                    <th style={styles.th}>Advance Bal.</th>
                                    <th style={styles.th}>Base Salary</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStaff.map(emp => (
                                    <tr key={emp._id} className="staff-row">
                                        <td style={styles.td}>
                                            <div style={styles.nameContainer}>
                                                <div style={styles.avatar}>
                                                    {emp.profilePhoto ? 
                                                        <img src={`${IMAGE_BASE}/${emp.profilePhoto}`} style={styles.avatarImg} alt="" /> 
                                                        : emp.name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <div style={styles.nameText}>{emp.name}</div>
                                                    <small style={{ color: '#8f9bba' }}>ID: {emp.employeeId}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={styles.td}><span style={styles.postBadge}>{emp.post}</span></td>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <a href={`${IMAGE_BASE}/${emp.aadharFront}`} target="_blank" rel="noreferrer" style={styles.docBtn} title="Aadhar Front">Front</a>
                                                <a href={`${IMAGE_BASE}/${emp.aadharBack}`} target="_blank" rel="noreferrer" style={styles.docBtn} title="Aadhar Back">Back</a>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ color: emp.advanceBalance < 0 ? '#ef4444' : '#e67e22', fontWeight: '700' }}>
                                                ₹{Math.abs(emp.advanceBalance || 0)}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.salaryValue}>₹{emp.baseSalary}</div>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                <button title="Give Advance" style={{ ...styles.iconBtn, color: '#f59e0b' }} onClick={() => handleGiveAdvance(emp._id)}>
                                                    <Wallet size={18} />
                                                </button>
                                                <button title="Payroll Dashboard" style={{ ...styles.iconBtn, color: '#10b981' }} onClick={() => handleViewDashboard(emp)}>
                                                    <ExternalLink size={18} />
                                                </button>
                                                <button title="Edit" style={{ ...styles.iconBtn, color: '#6366f1' }} onClick={() => handleEditClick(emp)}>
                                                    <Edit size={18} />
                                                </button>
                                                <button title="Delete" style={{ ...styles.iconBtn, color: '#ef4444' }} onClick={() => handleDelete(emp._id)}>
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={styles.emptyMsg}>No employees found matching your search.</div>
                )}
            </div>

            {isSalaryModalOpen && (
                <EmployeeDashboard data={payrollDetails} onClose={() => setIsSalaryModalOpen(false)} />
            )}

            <style>{`
                .spin { animation: rotate 1s linear infinite; }
                @keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .staff-row { transition: 0.2s; border-bottom: 1px solid #f1f5f9; }
                .staff-row:hover { background-color: #F9FAFB; }
                ::-webkit-scrollbar { height: 6px; }
                ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            `}</style>
        </div>
    );
};

// Styles object (standardized for Enterprise look)
const styles = {
    container: { backgroundColor: '#F4F7FE', minHeight: '100vh', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' },
    headerCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #E0E5F2', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' },
    headerFlex: { display: 'flex', justifyContent: 'space-between' },
    title: { fontSize: '20px', fontWeight: '800', margin: 0, color: '#1B2559' },
    subtitle: { fontSize: '13px', color: '#707EAE', margin: '4px 0 0 0' },
    btnSync: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', backgroundColor: '#fff', border: '1px solid #E0E5F2', borderRadius: '12px', cursor: 'pointer', transition: '0.3s' },
    btnAdd: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#4318FF', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' },
    tableCard: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #E0E5F2', overflow: 'hidden' },
    mobileContainer: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #E0E5F2', overflow: 'hidden' },
    searchBarContainer: { marginBottom: '20px' },
    searchWrapper: { position: 'relative' },
    searchIcon: { position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#A3AED0' },
    searchInput: { width: '100%', padding: '14px 14px 14px 45px', borderRadius: '14px', border: '1px solid #E0E5F2', outline: 'none', boxSizing: 'border-box', fontSize: '14px', color: '#1B2559' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '16px', backgroundColor: '#F8FAFC', fontSize: '12px', textTransform: 'uppercase', color: '#A3AED0', fontWeight: '800', letterSpacing: '0.05em' },
    td: { padding: '16px', fontSize: '14px', color: '#1B2559' },
    nameContainer: { display: 'flex', alignItems: 'center', gap: '12px' },
    avatar: { width: '40px', height: '40px', borderRadius: '12px', backgroundColor: '#F4F7FE', color: '#4318FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', overflow: 'hidden', flexShrink: 0, fontSize: '16px' },
    avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    nameText: { fontWeight: '700', color: '#1B2559' },
    postBadge: { backgroundColor: '#F4F7FE', color: '#4318FF', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '700' },
    salaryValue: { fontWeight: '700', color: '#1B2559' },
    docBtn: { textDecoration: 'none', backgroundColor: '#fff', color: '#4318FF', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E0E5F2', fontSize: '11px', fontWeight: '600' },
    iconBtn: { border: 'none', background: 'none', cursor: 'pointer', padding: '6px', borderRadius: '8px', transition: '0.2s' },
    emptyMsg: { textAlign: 'center', padding: '40px', color: '#A3AED0', fontWeight: '600' }
};

export default StaffList;